import * as React from 'react'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { Spinner, SpinnerSize } from '@fluentui/react'
import { useTranslation } from 'react-i18next'
import useAxios from '../../hooks/useAxios'
import { Destination, IGraphArrayResponse } from '../../context/AxiosContext'
import { driveItemOverviewProperties, formatFolderName } from '../../helpers/documentHelper'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import { Axios } from 'axios'
import {
    FlatTree,
    FlatTreeItemProps,
    FlatTreeProps,
    HeadlessFlatTreeItem,
    HeadlessFlatTreeItemProps,
    mergeClasses,
    TreeItemValue,
    TreeSelectionValue,
    useHeadlessFlatTree_unstable,
} from '@fluentui/react-components'
import { HeadlessFlatTree, TreeCheckedChangeData } from '@fluentui/react-tree'
import SimpleDocumentFlatTreeItem from './SimpleDocumentFlatTreeItem'
import { DocumentSortOrder } from '../../config/documentConfig'
import { DateTime } from 'luxon'
import FileDocumentSearchBox from '../../v2/pages/File/ViewFileTabs/FileDocumentSearchBox'
import { useApplicationStyles } from '../../pages/App'

export enum DocumentSelectionStrategy {
    Single = 'SINGLE',
    HierarchyBackTrack = 'HIERARCHY_BACK_TRACK',
}

export type DocumentFlatTreeProps = FlatTreeProps & {
    rootId: string
    sortOrder: DocumentSortOrder
    selectionStrategy: DocumentSelectionStrategy
    selectedOptions: string[]
    onOptionsSelect: (ids: string[], items: FolderDriveItem[]) => void
    setIsLoading?: (isLoading: boolean) => void
    onDriveItemsLoaded?: (allItems: FolderDriveItem[], filteredItems?: FolderDriveItem[]) => void
    setUpdateDriveItems?: (
        setter: (addDriveItems: DriveItem[], removeDriveItems: DriveItem[], filter?: string, sort?: 'asc' | 'desc') => void,
    ) => void
    setReloadDriveItems?: (setter: () => void) => void
    treeItemContent?: (flatTree: any, props: DocumentFlatTreeProps, itemProps: HeadlessFlatTreeItem<FolderDriveItem>) => React.ReactNode
}

export type DocumentFlatTreeItemProps = FlatTreeItemProps &
    DocumentFlatTreeProps & {
        item: FolderDriveItem
        flatTreeHook: HeadlessFlatTree<FolderDriveItem>
        // size: number | 'small' | 'medium'
    }

export type FolderDriveItem = HeadlessFlatTreeItemProps &
    DriveItem & {
        parent: FolderDriveItem
        displayName: string
        children: FolderDriveItem[]
        childCount?: number
    }

const mapDriveItem = (item: DriveItem, parentItem: FolderDriveItem, rootId: string): FolderDriveItem => {
    return {
        ...item,
        value: item.id,
        parentValue: item.parentReference.id === rootId ? undefined : item.parentReference.id,
        itemType: item.folder ? 'branch' : 'leaf',
        parent: parentItem,
        displayName: item.folder ? formatFolderName(item.name) : item.name,
        children: [],
        childCount: item.folder?.childCount,
    }
}

const fetchFolderDriveItemChildren = (
    axios: Axios,
    id: string,
    nextLink?: string,
    foldersOnly?: boolean,
): Promise<IGraphArrayResponse<DriveItem>> => {
    return nextLink
        ? (axios.get(nextLink, { baseURL: Destination.Documents, headers: { SuppressErrors: 'true' } }) as Promise<IGraphArrayResponse<DriveItem>>)
        : (
              axios.get(`/items/${id}/children`, {
                  baseURL: Destination.Documents,
                  params: { $select: driveItemOverviewProperties.join(','), $filter: foldersOnly ? 'folder ne null' : undefined },
                  headers: { SuppressErrors: 'true' },
              }) as Promise<IGraphArrayResponse<DriveItem>>
          ).then((response: IGraphArrayResponse<DriveItem>) => {
              if (response.data['@odata.nextLink']) {
                  return fetchFolderDriveItemChildren(axios, id, response.data['@odata.nextLink'], foldersOnly).then(response2 => ({
                      ...response,
                      ...response2,
                      data: { ...response.data, ...response2.data, value: [...response.data?.value, ...response2.data?.value] },
                  }))
              } else {
                  return response
              }
          })
}

const mapResponse = (
    axios: Axios,
    driveItem: DriveItem,
    parentItem: FolderDriveItem,
    rootId: string,
    foldersOnly: boolean,
): Promise<FolderDriveItem[]> => {
    return Promise.resolve(mapDriveItem(driveItem, parentItem, rootId)).then(folderDriveItem => {
        if (folderDriveItem.childCount > 0) {
            return fetchFolderDriveItemChildren(axios, folderDriveItem.id, undefined, foldersOnly)
                .then(children => {
                    return children.data?.value?.flatMap(child => mapResponse(axios, child, folderDriveItem, rootId, foldersOnly)) || []
                })
                .then(result => Promise.all(result))
                .then(children => (children.length === 0 ? [] : children.reduce((a, b) => a.concat(b))))
                .then(children => {
                    return [
                        { ...folderDriveItem, children: children, childCount: children.filter(child => child.itemType === 'leaf').length },
                        ...children,
                    ]
                })
        } else {
            return Promise.resolve([folderDriveItem])
        }
    })
}

export const fetchFolderDriveItemTree = (axios: Axios, id: string, foldersOnly: boolean = false): Promise<FolderDriveItem[]> => {
    return axios
        .get<DriveItem>(`/items/${id}`, {
            baseURL: Destination.Documents,
            params: { $select: driveItemOverviewProperties.join(',') },
            headers: { SuppressErrors: 'true' },
        })
        .then(response => {
            return mapResponse(axios, response.data, undefined, response.data.id, foldersOnly)
        })
}

const sortDriveItems = (
    driveItems: FolderDriveItem[],
    sortOrder: DocumentSortOrder,
    rootId: string,
): ((a: FolderDriveItem, b: FolderDriveItem) => number) => {
    return (a: FolderDriveItem, b: FolderDriveItem) => {
        //Put first level items at the top.
        if (a.parent?.id === rootId && b.parent?.id !== rootId) {
            return -1
        } else if (b.parent?.id === rootId && a.parent?.id !== rootId) {
            return 1
        }

        const parentAIndex = driveItems.indexOf(driveItems.find(item => item.id === a.parent.id))
        const parentBIndex = driveItems.indexOf(driveItems.find(item => item.id === b.parent.id))
        if (parentAIndex < parentBIndex) {
            return parentAIndex
        } else if (parentAIndex > parentBIndex) {
            return parentBIndex
        }

        //Put branches at the top, leafs at the bottom.
        //Specifically used for where folders and documents are children of a folder.
        if (a.itemType === 'branch' && b.itemType !== 'branch') {
            return -1
        } else if (b.itemType === 'branch' && a.itemType !== 'branch') {
            return 1
        }

        //Folders are always sorted alphabetically, regardless of the chosen sort order for files.
        if (a.itemType === 'branch' && b.itemType === 'branch') {
            return a.name.localeCompare(b.name)
        }

        switch (sortOrder) {
            case DocumentSortOrder.NameAsc:
                return a.name.localeCompare(b.name)
            case DocumentSortOrder.NameDesc:
                return b.name.localeCompare(a.name)
            case DocumentSortOrder.ModifiedAsc:
                return DateTime.fromISO(a.lastModifiedDateTime).toMillis() < DateTime.fromISO(b.lastModifiedDateTime).toMillis() ? -1 : 1
            case DocumentSortOrder.ModifiedDesc:
                return DateTime.fromISO(b.lastModifiedDateTime).toMillis() < DateTime.fromISO(a.lastModifiedDateTime).toMillis() ? -1 : 1
        }

        return 0
    }
}

export default function DocumentFlatTree(props: DocumentFlatTreeProps): ReactElement {
    const axios = useAxios()
    const appStyles = useApplicationStyles()
    const { t: translate } = useTranslation()

    const [rootFolder, setRootFolder] = useState<FolderDriveItem>(undefined)
    const [allDriveItems, setAllDriveItems] = useState<FolderDriveItem[]>(undefined)
    const [filteredDriveItems, setFilteredDriveItems] = useState<FolderDriveItem[]>(undefined)
    const [checkedItems, setCheckedItems] = useState<Map<TreeItemValue, TreeSelectionValue>>(new Map())
    const [openItems, setOpenItems] = useState<string[]>([])

    const driveItems: FolderDriveItem[] = useMemo(
        () => (filteredDriveItems ? filteredDriveItems : allDriveItems),
        [filteredDriveItems, allDriveItems],
    )

    const updateDriveItemsFunc = useCallback(
        (addDriveItems?: DriveItem[], removeDriveItems?: DriveItem[]) => {
            setAllDriveItems(prevItems => {
                const removeDriveItemIds = removeDriveItems?.map(driveItem => driveItem.id) || []

                const existingDriveItems = [...prevItems.filter(prevItem => !removeDriveItemIds.includes(prevItem.id))]

                const result = [
                    ...existingDriveItems,
                    ...(addDriveItems?.map(driveItem =>
                        mapDriveItem(
                            driveItem,
                            prevItems?.find(folderDriveItem => driveItem.parentReference.id === folderDriveItem.id),
                            props.rootId,
                        ),
                    ) || []),
                ]
                result.sort(sortDriveItems(result, props.sortOrder, props.rootId))
                return result
            })
        },
        [props.sortOrder, props.rootId],
    )

    const reloadDriveItemsFunc = useCallback(() => {
        setFilteredDriveItems(undefined)
        if (props.rootId) {
            props.setIsLoading?.(true)
            fetchFolderDriveItemTree(axios, props.rootId).then(result => {
                const root = result.shift()
                setRootFolder(root)
                result.sort(sortDriveItems(result, props.sortOrder, props.rootId))
                setAllDriveItems(result)
                props.setIsLoading?.(false)
            })
        } else {
            setAllDriveItems(undefined)
        }
    }, [props.sortOrder, props.rootId])

    useEffect(() => {
        setFilteredDriveItems(prevItems => {
            if (prevItems) {
                const result = [...prevItems]
                result.sort(sortDriveItems(result, props.sortOrder, props.rootId))
                return result
            }
            return prevItems
        })
        setAllDriveItems(prevItems => {
            if (prevItems) {
                const result = [...prevItems]
                result.sort(sortDriveItems(result, props.sortOrder, props.rootId))
                return result
            }
            return prevItems
        })
    }, [props.sortOrder, props.rootId])

    useEffect(() => {
        props.onDriveItemsLoaded?.(allDriveItems, filteredDriveItems)
    }, [allDriveItems, filteredDriveItems])

    useEffect(() => {
        props.setUpdateDriveItems?.((addDriveItems: DriveItem[], removeDriveItems: DriveItem[]) =>
            updateDriveItemsFunc(addDriveItems, removeDriveItems),
        )
        props.setReloadDriveItems?.(() => reloadDriveItemsFunc())

        return () => props.setUpdateDriveItems?.(() => {}) // Cleanup
    }, [])

    const flatTree = useHeadlessFlatTree_unstable(driveItems, {
        selectionMode: 'multiselect',
        onCheckedChange: (_, data: TreeCheckedChangeData) => {
            let newCheckedItems: Map<TreeItemValue, TreeSelectionValue> = new Map(checkedItems)
            let newSelectedDriveItemIds: string[] = []
            let newSelectedDriveItems: FolderDriveItem[] = []

            switch (props.selectionStrategy) {
                case DocumentSelectionStrategy.Single: {
                    if (data.checked) {
                        newCheckedItems.set(data.value, data.checked)
                    } else {
                        newCheckedItems.delete(data.value)
                    }
                    newSelectedDriveItemIds = Array.from(newCheckedItems.keys()) as string[]
                    newSelectedDriveItems = Array.from(newCheckedItems)
                        .filter(([, checkedValue]) => checkedValue === true)
                        .flatMap(([checkedItem]) => allDriveItems.filter(item => checkedItem === item.id))

                    break
                }
                case DocumentSelectionStrategy.HierarchyBackTrack: {
                    newCheckedItems = new Map(data.checkedItems)

                    newSelectedDriveItemIds = Array.from(data.checkedItems)
                        .filter(
                            ([checkedItem, checkedValue]) =>
                                allDriveItems.find(item => item.value === checkedItem)?.itemType === 'leaf' && checkedValue === true,
                        )
                        .map(([item]) => item as string)
                    newSelectedDriveItems = allDriveItems.filter(item => newSelectedDriveItemIds.includes(item.id))

                    break
                }
            }

            props.onOptionsSelect?.(newSelectedDriveItemIds, newSelectedDriveItems)
        },
        checkedItems: checkedItems,
        openItems: openItems,
        onOpenChange: (_, data) => {
            if (data.open) {
                setOpenItems(oldItems => [...new Set([...oldItems, data.value.toString()])])
            } else {
                setOpenItems(oldItems => oldItems.filter(item => item !== data.value.toString()))
            }
        },
    })

    useEffect(() => {
        reloadDriveItemsFunc()
    }, [props.rootId])

    useEffect(() => {
        if (allDriveItems) {
            switch (props.selectionStrategy) {
                case DocumentSelectionStrategy.Single: {
                    setCheckedItems(
                        new Map(
                            props.selectedOptions
                                ?.map(value => allDriveItems.find(item => item.value === value))
                                .filter(Boolean)
                                .map(value => [value.id, true as TreeSelectionValue]),
                        ),
                    )
                    break
                }
                case DocumentSelectionStrategy.HierarchyBackTrack: {
                    setCheckedItems(
                        new Map(
                            props.selectedOptions
                                ?.map(value => allDriveItems.find(item => item.value === value))
                                .filter(Boolean)
                                .flatMap(value => {
                                    const hierarchy: [TreeItemValue, TreeSelectionValue][] = []
                                    hierarchy.push([value.id, true])
                                    let parentItem = allDriveItems.find(item => item.value === value.parentValue)
                                    while (parentItem !== undefined) {
                                        hierarchy.push([parentItem.id, 'mixed'])
                                        parentItem = allDriveItems.find(item => item.value === parentItem.parentValue)
                                    }
                                    return hierarchy
                                }) || [],
                        ),
                    )
                    break
                }
            }
        }
    }, [driveItems, props.selectedOptions])

    if (!allDriveItems) {
        return <Spinner label={translate('base.loading')} size={SpinnerSize.xSmall} labelPosition="right" />
    }

    return (
        <div className={mergeClasses(appStyles.flexColumn, appStyles.gapS)}>
            <FileDocumentSearchBox
                {...props}
                placeholderKey={'file.pivot.document.search.placeholder'}
                componentProps={{ appearance: 'underline' }}
                rootFolder={rootFolder}
                driveItems={allDriveItems}
                setFilteredDriveItems={searchResults => {
                    const sortedResults = searchResults.sort(sortDriveItems(searchResults, props.sortOrder, props.rootId))
                    setFilteredDriveItems(sortedResults)
                    props.onOptionsSelect?.([], [])
                    setOpenItems(sortedResults.filter(item => item.itemType === 'branch').map(item => item.id))
                }}
                reset={() => {
                    setFilteredDriveItems(undefined)
                    props.onOptionsSelect?.([], [])
                    setOpenItems([])
                    reloadDriveItemsFunc()
                }}
            />
            <FlatTree {...props} {...flatTree.getTreeProps()} aria-label={'FileDocumentTree'}>
                {Array.from(flatTree.items(), flatTreeItem => {
                    return (
                        props.treeItemContent?.(flatTree, props, flatTreeItem) || (
                            <SimpleDocumentFlatTreeItem
                                key={flatTreeItem.value}
                                {...flatTreeItem.getTreeItemProps()}
                                {...props}
                                item={flatTreeItem.getTreeItemProps()}
                                flatTreeHook={flatTree}
                                itemType={flatTreeItem.itemType}
                                size={props.size}
                            />
                        )
                    )
                })}
            </FlatTree>
        </div>
    )
}
